import React, { useEffect, useState } from 'react';
import { db, auth, functions, storage } from '../../firebaseConfig';
import { httpsCallable } from 'firebase/functions';
import { doc, getDoc, collection, query, where, orderBy, getDocs, addDoc } from 'firebase/firestore';
import { ref, getDownloadURL, StorageReference } from 'firebase/storage';
import { useSubAgentAuth } from '../../pages/Clients/SubAgent/SubAgentAuthContext';
import { useAuth } from '../../AuthContext';

// redux
import { useAppSelector } from '../../redux/store';

import jsPDF from 'jspdf';
import generatePDF, { Resolution, Margin, Options, usePDF } from 'react-to-pdf';
import getCurrentScreen from '../../Helper/GetScreen';
import GradientSubmitButton from '../GradientSubmitButton';

interface ForexRemittanceProps {
  write: boolean;
}

interface Student {
  id: string;
  name: string;
  email: string;
  number: string;
}

interface ConversionRateResponse {
  educationLoan: boolean;
  currencyCode: string;
  quantity: 0;
  remittanceRate: 0;
  TxnAmount: 0;
  GST: 0;
  TCS: 0;
  NostroCharge: 0;
}

interface Remittance {
  id: string;
  studentId: string;
  studentName: string;
  name: string;
  email: string;
  phone: string;
  foreignCurrency: string;
  quantity: string;
  educationLoan: boolean;
  markUp: string;
  purpose: string;
  status: string;
  conversionRate: ConversionRateResponse;
}

const ForexRemittance = ({ write }: ForexRemittanceProps) => {

  const { isAdmin, isManager, isStaff } = useAuth();
  const { currentSubAgent } = useSubAgentAuth();

  const currentScreen = getCurrentScreen(useAppSelector(state => state.screen.currentScreen), isAdmin, isManager, isStaff);

  const getConvertionRate = httpsCallable(functions, 'getConversionRate');

  const [remittances, setRemittances] = useState<Remittance[]>([]);

  // currency data with country name and currency code
  const currencyData = [
    { country: 'United States', code: 'USD' },
    { country: 'United Kingdom', code: 'GBP' },
    { country: 'Eurozone', code: 'EUR' },
    { country: 'Australia', code: 'AUD' },
    { country: 'Canada', code: 'CAD' },
    { country: 'Singapore', code: 'SGD' },
  ];

  const purposeOptions = [
    { value: 'Personal Visit / Leisure Travel' },
    { value: 'Immigration' },
    { value: 'Business Travel' },
    { value: 'Medical Treatment' },
    { value: 'Overseas Education' }
  ];

  const [isPopupOpen, setIsPopupOpen] = useState(false);
  const [quotePopupOpen, setQuotePopupOpen] = useState(false);
  const [isQuoteLoading, setIsQuoteLoading] = useState(false);
  const [newRemittance, setNewRemittance] = useState({
    name: '',
    email: '',
    phone: '',
    foreignCurrency: '',
    quantity: '',
    educationLoan: true,
    markUp: '',
    purpose: 'Overseas Education',
    status: 'Pending'
  });

  const [newQuote, setNewQuote] = useState({
    name: '',
    foreignCurrency: '',
    quantity: '',
    educationLoan: true,
    markUp: '',
    purpose: 'Overseas Education',
  });

  const [convertionData, setConvertionData] = useState<ConversionRateResponse[]>([]);

  const [selectedStudent, setSelectedStudent] = useState<Student>(
    { id: '', name: '', email: '', number: '' }
  );
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [isStudentDropdownOpen, setIsStudentDropdownOpen] = useState(false);
  const [students, setStudents] = useState<any[]>([]);
  const [isStudentLoading, setIsStudentLoading] = useState(false);

  const toggleDropdown = (dropdown: string, forceOpen?: boolean) => {
    if (dropdown === 'student') {
      setIsStudentDropdownOpen((prev) => forceOpen !== undefined ? forceOpen : !prev);
    }
  }

  // student search
  const handleStudentSearch = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.toUpperCase();
    setSearchTerm(value);

    // toogle student dropdown
    if (!isStudentDropdownOpen) {
      toggleDropdown("student", true);
    }


    if (value.length < 3) {
      setStudents([]);
      return;
    }

    setIsStudentLoading(true);
    try {
      const currentUser = auth.currentUser;
      if (!currentUser) return;

      let companyId: string | undefined;
      // Get user document to get companyId
      if (currentSubAgent) {
        companyId = currentSubAgent.companyId;
      } else {
        const userDoc = await getDoc(doc(db, 'users', currentUser.uid));
        companyId = userDoc.data()?.companyId;
      }
      if (!companyId) return;

      let studentsQuery = query(
        collection(db, `companies/${companyId}/leads`),
        where('name', '>=', value),
        where('name', '<=', value + '\uf8ff'),
        orderBy('name')
      );

      if (currentSubAgent) {
        studentsQuery = query(
          collection(db, `companies/${currentSubAgent.companyId}/leads`),
          where('name', '>=', value),
          where('name', '<=', value + '\uf8ff'),
          where('subAgentId', '==', currentSubAgent.uid),
          orderBy('name')
        );
      } else if (isManager && currentScreen === 'Manager') {
        const usersQuery = query(
          collection(db, "users"),
          where("companyId", "==", companyId),
          where("managerIds", "array-contains", currentUser?.uid)
        );

        const usersSnapshot = await getDocs(usersQuery);
        const userIds = usersSnapshot.docs.map(doc => doc.id);
        studentsQuery = query(
          collection(db, `companies/${companyId}/leads`),
          where('name', '>=', value),
          where('name', '<=', value + '\uf8ff'),
          where('assignedTo', 'in', userIds),
          orderBy('name')
        );
      }


      const studentsSnapshot = await getDocs(studentsQuery);
      const studentList = studentsSnapshot.docs.map((doc) => ({
        id: doc.id,
        name: doc.data().name,
        email: doc.data().email,
        number: doc.data().number,
      }));

      setStudents(studentList);
    } catch (error) {
      console.error("Error fetching students:", error);
    } finally {
      setIsStudentLoading(false);
    }
  };


  const [isLogoLoading, setIsLogoLoading] = useState(true);
  const [companyLogo, setCompanyLogo] = useState<string | null>(null);
  const [companyId, setCompanyId] = useState<string | null>(null);
  const [companyName, setCompanyName] = useState<string | null>(null);
  const [companyEmail, setCompanyEmail] = useState<string | null>(null);

  useEffect(() => {
    const fetchCompanyId = async () => {
      const currentUser = auth.currentUser;
      if (!currentUser) return;

      let companyId: string | undefined;
      // Get user document to get companyId
      if (currentSubAgent) {
        companyId = currentSubAgent.companyId;
      } else {
        const userDoc = await getDoc(doc(db, 'users', currentUser.uid));
        companyId = userDoc.data()?.companyId;
      }
      setCompanyId(companyId || null);
      if (!companyId) return;
      const companyDoc = await getDoc(doc(db, 'companies', companyId));
      if (companyDoc.exists()) {
        setCompanyName(companyDoc.data().name || "Unknown Company");
        setCompanyEmail(companyDoc.data().email || "");
      } else {
        console.log("Company not found");
      }
    };

    fetchCompanyId();
  }, []);

  useEffect(() => {
    const fetchCompanyLogo = async (companyId: string) => {
      setIsLogoLoading(true);
      try {
        const logoRef: StorageReference = ref(storage, `companies/logos/${companyId}.png`);
        const url = await getDownloadURL(logoRef);
        setCompanyLogo(url);
      } catch (error) {
        console.log("No company logo found or error fetching logo");
        setCompanyLogo(null);
      } finally {
        setIsLogoLoading(false);
      }
    };

    if (companyId) {
      fetchCompanyLogo(companyId);
    }
  }, [companyId]);

  const handleInitiateRemittance = () => {
    setIsPopupOpen(true);
  };

  const handleQuote = () => {
    setQuotePopupOpen(true);
  };

  const handleMarkUpChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    // if value is like this eg: .5 or .10 then add 0 in front of it
    if (value.startsWith('.')) {
      setNewQuote({ ...newQuote, markUp: '0' + value });
      setNewRemittance({ ...newRemittance, markUp: '0' + value });
    } else {
      setNewQuote({ ...newQuote, markUp: value });
      setNewRemittance({ ...newRemittance, markUp: value });
    }
  }

  const closePopup = () => {
    setIsPopupOpen(false);
    setQuotePopupOpen(false);
    setSelectedStudent({ id: '', name: '', email: '', number: '' });
    setSearchTerm('');
    setIsStudentDropdownOpen(false);
    setStudents([]);
    setIsStudentLoading(false);
    setIsQuoteLoading(false);
    // Reset the form fields
    setNewRemittance({
      name: '',
      email: '',
      phone: '',
      foreignCurrency: '',
      quantity: '',
      educationLoan: true,
      markUp: '',
      purpose: 'Overseas Education',
      status: 'Pending'
    });

    setNewQuote({
      name: '',
      foreignCurrency: '',
      quantity: '',
      educationLoan: true,
      markUp: '',
      purpose: 'Overseas Education',
    });
  };

  useEffect(() => {
    // set remittance email and phone number to the selected student email and phone number
    if (selectedStudent) {
      setNewRemittance({
        ...newRemittance,
        email: selectedStudent.email,
        phone: selectedStudent.number,
      });
    }
  }, [selectedStudent]);


  // fetch remittance data
  useEffect(() => {
    const fetchRemittanceData = async () => {
      const currentUser = auth.currentUser;
      if (!currentUser) return;

      let companyId: string | undefined;
      // Get user document to get companyId
      if (currentSubAgent) {
        companyId = currentSubAgent.companyId;
      } else {
        const userDoc = await getDoc(doc(db, 'users', currentUser.uid));
        companyId = userDoc.data()?.companyId;
      }
      if (!companyId) return;

      const remittanceRef = query(collection(db, `forexRemittanceRequests`), where('companyId', '==', companyId));
      const q = query(remittanceRef, orderBy('createdAt', 'desc'));
      const querySnapshot = await getDocs(q);
      const remittanceList: Remittance[] = [];
      querySnapshot.forEach((doc) => {
        remittanceList.push({ id: doc.id, ...doc.data() } as Remittance);
      });
      console.log('Remittance List:', remittanceList);

      setRemittances(remittanceList);
    };

    fetchRemittanceData();
  }, [currentSubAgent, isPopupOpen]);

  const handleAddRemittance = async () => {
    // Validate form
    if (!newRemittance.name || !newRemittance.email || !newRemittance.phone || !newRemittance.educationLoan || !newRemittance.foreignCurrency || !newRemittance.quantity || !newRemittance.purpose) {
      alert('Please fill in all required fields');
      return;
    }

    setIsQuoteLoading(true);

    // Get quote for the remittance
    try {
      const response = getConvertionRate({
        foreignCurrency: newRemittance.foreignCurrency,
        quantity: parseFloat(newRemittance.quantity) || 0,
        educationLoan: newRemittance.educationLoan,
        markUp: parseFloat(newRemittance.markUp) || 0,
        purpose: newRemittance.purpose,
      });

      // Handle the response
      const conversionRate = ((await response).data as { conversionRate: any }).conversionRate;
      console.log('Conversion Rate:', conversionRate);

      // save the convertion rate data along with student data and companyId in forexRemittanceRequests collection.
      const currentUser = auth.currentUser;
      if (!currentUser) return;
      let companyId: string | undefined;
      // Get user document to get companyId
      if (currentSubAgent) {
        companyId = currentSubAgent.companyId;
      } else {
        const userDoc = await getDoc(doc(db, 'users', currentUser.uid));
        companyId = userDoc.data()?.companyId;
      }

      if (!companyId) return;
      const remittanceData = {
        ...newRemittance,
        conversionRate: conversionRate,
        companyId: companyId,
        userId: currentUser.uid,
        studentId: selectedStudent.id,
        studentName: newRemittance.name || selectedStudent.name,
        studentEmail: newRemittance.email || selectedStudent.email,
        studentPhone: newRemittance.phone || selectedStudent.number,
        status: newRemittance.status,
        createdAt: new Date(),
      };

      console.log('Remittance Data:', remittanceData);

      const remittanceRef = collection(db, `forexRemittanceRequests`);
      await addDoc(remittanceRef, remittanceData);
      console.log('Remittance data saved successfully:', remittanceData);
      alert('Remittance data saved successfully');
      setIsPopupOpen(false);
      closePopup();
    } catch (error) {
      console.error('Error saving remittance data:', error);
      alert('Failed to save remittance data. Please try again.');
    }
  };

  const handleGetQuote = async () => {
    setIsQuoteLoading(true);
    // Validate form
    if (!newQuote.name || !newQuote.foreignCurrency || !newQuote.quantity || !newQuote.purpose) {
      alert('Please fill in all required fields');
      return;
    }

    // log the new quote
    console.log('New Quote:', newQuote);

    try {
      const response = await getConvertionRate({
        foreignCurrency: newQuote.foreignCurrency,
        quantity: parseFloat(newQuote.quantity) || 0,
        educationLoan: newQuote.educationLoan,
        markUp: parseFloat(newQuote.markUp) || 0,
        purpose: newQuote.purpose,
      });

      // Handle the response
      const conversionRate = (response.data as { conversionRate: any }).conversionRate;
      console.log('Conversion Rate:', conversionRate);
      setConvertionData([conversionRate]);
      setIsQuoteLoading(false);

    } catch (error) {
      console.error('Error fetching conversion rate:', error);
      alert('Failed to fetch conversion rate. Please try again.');
      setIsQuoteLoading(false);
    }
  }


  const options: Options = {
    // default is `save`
    method: 'save',
    // default is Resolution.MEDIUM = 3, which should be enough, higher values
    // increases the image quality but also the size of the PDF, so be careful
    // using values higher than 10 when having multiple pages generated, it
    // might cause the page to crash or hang.
    resolution: Resolution.NORMAL,
    page: {
      // margin is in MM, default is Margin.NONE = 0
      margin: Margin.SMALL,
      // default is 'A4'
      format: 'letter',
      // default is 'portrait'
      orientation: 'portrait',
    },
    canvas: {
      // default is 'image/jpeg' for better size performance
      mimeType: 'image/png',
      qualityRatio: 1,
      useCORS: true,
      logging: false,
    },
    // Customize any value passed to the jsPDF instance and html2canvas
    // function. You probably will not need this and things can break, 
    // so use with caution.
    overrides: {
      // see https://artskydj.github.io/jsPDF/docs/jsPDF.html for more options
      pdf: {
        compress: true,
        hotfixes: ['px_scaling'],
        unit: 'mm',
        format: 'letter',
        orientation: 'portrait',
        putOnlyUsedFonts: true,
        floatPrecision: 16, // default is 16, can be set to 2 for better performance
      },
      // see https://html2canvas.hertzen.com/configuration for more options
      canvas: {
        useCORS: true,
        allowTaint: true,
        foreignObjectRendering: false,
        scale: 2, // default is 2, can be set to 1 for better performance
      }
    },
  };

  const { toPDF, targetRef } = usePDF({ filename: `quotation_${selectedStudent.name || 'student'}.pdf`, ...options });

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold text-[#02385E] font-Montserrat">Forex Remittance Services</h1>
        {write && (
          <div className="flex items-center gap-4">
            <button
              onClick={handleQuote}
              className="px-4 py-2 text-white font-semibold rounded-full flex items-center gap-2"
              style={{ background: 'linear-gradient(180deg, #FFA782 0%, #FC583D 100%)', }}  >
              <span style={{ fontFamily: 'Montserrat', fontSize: '20px', fontWeight: '500', lineHeight: '24.38px', }}>
                Create quote
              </span>
              <img src={`${process.env.PUBLIC_URL}/images/desktop/staffadd.svg`} alt="Add Remittance Icon" className="w-4 h-4 ml-2" />
            </button>
            <button
              onClick={handleInitiateRemittance}
              className="px-4 py-2 text-white font-semibold rounded-full flex items-center gap-2"
              style={{ background: 'linear-gradient(180deg, #FFA782 0%, #FC583D 100%)', }}  >
              <span style={{ fontFamily: 'Montserrat', fontSize: '20px', fontWeight: '500', lineHeight: '24.38px', }}>
                Initiate Remittance
              </span>
              <img src={`${process.env.PUBLIC_URL}/images/desktop/staffadd.svg`} alt="Add Remittance Icon" className="w-4 h-4 ml-2" />
            </button>
          </div>
        )}
      </div>

      <div className="rounded-xl overflow-hidden bg-white shadow-lg">
        <div className="overflow-x-auto relative">
          <table className="min-w-full table-auto border-collapse">
            <thead>
              <tr className="bg-gradient-to-r from-[#02385E] to-[#02385E]/90">
                <th className="px-4 py-3 text-left text-xs font-medium text-white uppercase tracking-wider border-r border-[#ffffff20] font-Montserrat">Name</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-white uppercase tracking-wider border-r border-[#ffffff20] font-Montserrat">Phone</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-white uppercase tracking-wider border-r border-[#ffffff20] font-Montserrat">Email</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-white uppercase tracking-wider border-r border-[#ffffff20] font-Montserrat">Quantity</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-white uppercase tracking-wider border-r border-[#ffffff20] font-Montserrat">Mark Up</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-white uppercase tracking-wider border-r border-[#ffffff20] font-Montserrat">Status</th>
                <th className="px-4 py-3 text-center text-xs font-medium text-white uppercase tracking-wider border-r border-[#ffffff20] font-Montserrat">View Quote</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {remittances.map((remittance, index) => (
                <tr key={remittance.id} className={index % 2 === 0 ? 'bg-white' : 'bg-slate-50'}>
                  <td className="px-4 py-2.5 whitespace-nowrap border-r border-gray-100 text-neutral-600 text-left font-Montserrat">
                    <div className="truncate text-left" title={remittance.name}>{remittance.name}</div>
                  </td>
                  <td className="px-4 py-2.5 whitespace-nowrap border-r border-gray-100 text-neutral-600 text-left font-Montserrat">
                    <div className="truncate text-left" title={remittance.phone}>{remittance.phone}</div>
                  </td>
                  <td className="px-4 py-2.5 whitespace-nowrap border-r border-gray-100 text-neutral-600 text-left font-Montserrat">
                    <div className="truncate text-left" title={remittance.email}>{remittance.email}</div>
                  </td>
                  <td className="px-4 py-2.5 whitespace-nowrap border-r border-gray-100 text-neutral-600 text-left font-Montserrat">
                    <div className="truncate text-left" title={remittance.quantity}>{remittance.quantity}</div>
                  </td>
                  <td className="px-4 py-2.5 whitespace-nowrap border-r border-gray-100 text-neutral-600 text-left font-Montserrat">
                    <div className="truncate text-left" title={remittance.markUp}>{parseFloat(remittance.quantity) * parseFloat(remittance.markUp)}</div>
                  </td>
                  <td className="px-4 py-2.5 whitespace-nowrap border-r border-gray-100 text-neutral-600 text-left font-Montserrat">
                    <span className={`inline-block min-w-[80px] px-2 py-1 rounded-full ${remittance.status === 'Completed' ? 'bg-green-100 text-green-800' :
                      remittance.status === 'Pending' ? 'bg-yellow-100 text-yellow-800' :
                        remittance.status === 'Processing' ? 'bg-blue-100 text-blue-800' :
                          'bg-red-100 text-red-800'
                      }`}>
                      {remittance.status}
                    </span>
                  </td>

                  {/* Download button */}
                  <td className="px-4 py-2.5 whitespace-nowrap border-r border-gray-100 text-neutral-600 text-left font-Montserrat">
                    <img
                      src={`${process.env.PUBLIC_URL}/images/desktop/tdesign_file-download.svg`}
                      alt="download-icon"
                      className="w-4 h-4 mx-auto cursor-pointer"
                      onClick={() => { setConvertionData([remittance.conversionRate]); setSelectedStudent({ id: remittance.studentId, name: remittance.studentName, email: remittance.email, number: remittance.phone }) }}
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Add Remittance Popup */}
      {isPopupOpen && (
        <div className="fixed inset-0 bg-gray-500 bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white p-8 rounded-3xl w-[90%] md:w-[70%] lg:w-[50%] relative">
            <div className="top-0 left-0 right-0 h-[40px] flex items-center justify-between rounded-tl-[12px] rounded-tr-[12px] p-4">
              <div className="flex items-center gap-4">
                <div className="w-14 h-14 rounded-full bg-gradient-to-b from-[#00A6D9] to-[#3483BE] flex items-center justify-center">
                  <img src={`${process.env.PUBLIC_URL}/images/desktop/ph_student.svg`} alt="Student Icon" />
                </div>
                <h2 className="text-xl font-semibold text-black">Add Remittance Transaction</h2>
              </div>
              <div className="text-right">
                <button onClick={closePopup} className="absolute top-4 right-4 font-semibold text-3xl" style={{ background: 'linear-gradient(270deg, #A7C8E7 0%, #00A6D9 100%)', WebkitBackgroundClip: 'text', color: 'transparent', }}>
                  <img src={`${process.env.PUBLIC_URL}/images/desktop/close-btn.svg`} alt="Close" className="w-6 h-6" />
                </button>
              </div>
            </div>

            <div className="grid grid-cols-1 gap-6 mt-8">
              <div>
                <label className="block text-sm font-medium text-gray-700">Name <span className="text-red-500">*</span></label>
                <div className="relative">
                  <input
                    type="text"
                    placeholder="Search for Student"
                    value={searchTerm || newRemittance.name}
                    onChange={handleStudentSearch}
                    onClick={(e) => {
                      // Don't toggle the dropdown when clicking on the input field
                      // This prevents accidental closing while typing/searching
                      e.stopPropagation();
                      if (!isStudentDropdownOpen) {
                        toggleDropdown("student", true);
                      }
                    }}
                    className="border p-2 w-full mb-2"
                    style={{ borderRadius: '24px', border: '1px solid #3483BE' }}
                  />

                  {isStudentDropdownOpen && (
                    <div className="absolute z-10 bg-white border border-gray-300 rounded-lg shadow-lg mt-1 w-full max-h-[200px] overflow-y-auto">
                      {isStudentLoading ? (
                        <div className="absolute top-0 right-0 mt-2 mr-2">
                          <div className="w-5 h-5 border-2 border-gray-400 border-t-transparent rounded-full animate-spin"></div>
                        </div>
                      ) : students.length > 0 ? (
                        students.map((student) => (
                          <div
                            key={student.id}
                            className="p-2 hover:bg-gray-100 cursor-pointer"
                            onClick={() => {
                              setNewRemittance({ ...newRemittance, name: student.name });
                              setSelectedStudent(student);
                              setSearchTerm('');
                              setIsStudentDropdownOpen(false);
                            }}
                          >
                            {student.name}
                          </div>
                        )))
                        : searchTerm.length < 3 ? (
                          <div className="p-2 text-gray-500">Type at least 3 characters to search</div>
                        ) : (
                          <div className="p-2 text-gray-500">No students found</div>
                        )}
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-6 mt-4">
              <div>
                <label className="block text-sm font-medium text-gray-700">Email <span className="text-red-500">*</span></label>
                <input
                  type="email"
                  placeholder="Email Address"
                  value={newRemittance.email}
                  onChange={(e) => setNewRemittance({ ...newRemittance, email: e.target.value })}
                  className="border p-2 w-full mb-2"
                  style={{ borderRadius: '24px', border: '1px solid #3483BE' }}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Phone <span className="text-red-500">*</span></label>
                <input
                  type="text"
                  placeholder="Phone Number"
                  value={newRemittance.phone}
                  onChange={(e) => setNewRemittance({ ...newRemittance, phone: e.target.value })}
                  className="border p-2 w-full mb-2"
                  style={{ borderRadius: '24px', border: '1px solid #3483BE' }}
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-6 mt-4">
              <div>
                <label className="block text-sm font-medium text-gray-700">Currency <span className="text-red-500">*</span></label>
                <select
                  value={newRemittance.foreignCurrency}
                  onChange={(e) => setNewRemittance({ ...newRemittance, foreignCurrency: e.target.value })}
                  className="border p-2 w-full mb-2"
                  style={{ borderRadius: '24px', border: '1px solid #3483BE' }}
                >
                  <option value="">Select Currency</option>
                  {currencyData.map((currency) => (
                    <option key={currency.code} value={currency.code}>
                      {currency.country} ({currency.code})
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Quantity <span className="text-red-500">*</span></label>
                <input
                  type="number"
                  placeholder="Enter Quantity"
                  value={newRemittance.quantity}
                  onChange={(e) => setNewRemittance({ ...newRemittance, quantity: e.target.value })}
                  className="border p-2 w-full mb-2"
                  style={{ borderRadius: '24px', border: '1px solid #3483BE' }}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Educational Loan <span className="text-red-500">*</span></label>
                <select
                  value={newRemittance.educationLoan ? 'Yes' : 'No'}
                  onChange={(e) => setNewRemittance({ ...newRemittance, educationLoan: e.target.value === 'Yes' })}
                  className="border p-2 w-full mb-2"
                  style={{ borderRadius: '24px', border: '1px solid #3483BE' }}
                >
                  <option value="Yes">Yes</option>
                  <option value="No">No</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Purpose <span className="text-red-500">*</span></label>
                <select
                  value={newRemittance.purpose}
                  onChange={(e) => setNewRemittance({ ...newRemittance, purpose: e.target.value })}
                  className="border p-2 w-full mb-2"
                  style={{ borderRadius: '24px', border: '1px solid #3483BE' }}
                >
                  <option value="">Select Purpose</option>
                  {purposeOptions.map((purpose, index) => (
                    <option key={index} value={purpose.value}>
                      {purpose.value}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Mark Up </label>
                <input
                  type="number"
                  placeholder="Enter Mark Up (eg: 1, 0.5, 100)"
                  value={newRemittance.markUp}
                  onChange={(e) => handleMarkUpChange(e)}
                  className="border p-2 w-full mb-2"
                  style={{ borderRadius: '24px', border: '1px solid #3483BE' }}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Status</label>
                <select
                  value={newRemittance.status}
                  onChange={(e) => setNewRemittance({ ...newRemittance, status: e.target.value })}
                  className="border p-2 w-full mb-2"
                  style={{ borderRadius: '24px', border: '1px solid #3483BE' }}
                >
                  <option value="Pending">Pending</option>
                  <option value="Processing">Processing</option>
                  <option value="Completed">Completed</option>
                  <option value="Failed">Failed</option>
                </select>
              </div>
            </div>

            <div className="mt-8 flex justify-end">
              <GradientSubmitButton
                onClick={handleAddRemittance}
                loading={isQuoteLoading}
                label="Submit"
                loadingLabel="Submitting..."
                disabled={isQuoteLoading}
              />
            </div>
          </div>
        </div>
      )}
      {/* Create Quote Popup */}
      {quotePopupOpen && (
        <div className="fixed inset-0 bg-gray-500 bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white p-8 rounded-3xl w-[90%] md:w-[70%] lg:w-[50%] relative">
            <div className="top-0 left-0 right-0 h-[40px] flex items-center justify-between rounded-tl-[12px] rounded-tr-[12px] p-4">
              <div className="flex items-center gap-4">
                <div className="w-14 h-14 rounded-full bg-gradient-to-b from-[#00A6D9] to-[#3483BE] flex items-center justify-center">
                  <img src={`${process.env.PUBLIC_URL}/images/desktop/ph_student.svg`} alt="Student Icon" />
                </div>
                <h2 className="text-xl font-semibold text-black">Create Quote</h2>
              </div>
              <div className="text-right">
                <button onClick={closePopup} className="absolute top-4 right-4 font-semibold text-3xl" style={{ background: 'linear-gradient(270deg, #A7C8E7 0%, #00A6D9 100%)', WebkitBackgroundClip: 'text', color: 'transparent', }}>
                  <img src={`${process.env.PUBLIC_URL}/images/desktop/close-btn.svg`} alt="Close" className="w-6 h-6" />
                </button>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-6 mt-8">
              <div>
                <label className="block text-sm font-medium text-gray-700">Name <span className="text-red-500">*</span></label>
                <div className="relative">
                  <input
                    type="text"
                    placeholder="Search for Student"
                    value={searchTerm || newQuote.name}
                    onChange={handleStudentSearch}
                    onClick={(e) => {
                      // Don't toggle the dropdown when clicking on the input field
                      // This prevents accidental closing while typing/searching
                      e.stopPropagation();
                      if (!isStudentDropdownOpen) {
                        toggleDropdown("student", true);
                      }
                    }}
                    className="border p-2 w-full mb-2"
                    style={{ borderRadius: '24px', border: '1px solid #3483BE' }}
                  />

                  {isStudentDropdownOpen && (
                    <div className="absolute z-10 bg-white border border-gray-300 rounded-lg shadow-lg mt-1 w-full max-h-[200px] overflow-y-auto">
                      {isStudentLoading ? (
                        <div className="absolute top-0 right-0 mt-2 mr-2">
                          <div className="w-5 h-5 border-2 border-gray-400 border-t-transparent rounded-full animate-spin"></div>
                        </div>
                      ) : students.length > 0 ? (
                        students.map((student) => (
                          <div
                            key={student.id}
                            className="p-2 hover:bg-gray-100 cursor-pointer"
                            onClick={() => {
                              setNewQuote({ ...newQuote, name: student.name });
                              setSelectedStudent(student);
                              setSearchTerm('');
                              setIsStudentDropdownOpen(false);
                            }}
                          >
                            {student.name}
                          </div>
                        )))
                        : searchTerm.length < 3 ? (
                          <div className="p-2 text-gray-500">Type at least 3 characters to search</div>
                        ) : (
                          <div className="p-2 text-gray-500">No students found</div>
                        )}
                    </div>
                  )}
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Currency <span className="text-red-500">*</span></label>
                <select
                  value={newQuote.foreignCurrency}
                  onChange={(e) => setNewQuote({ ...newQuote, foreignCurrency: e.target.value })}
                  className="border p-2 w-full mb-2"
                  style={{ borderRadius: '24px', border: '1px solid #3483BE' }}
                >
                  <option value="">Select Currency</option>
                  {currencyData.map((currency) => (
                    <option key={currency.code} value={currency.code}>
                      {currency.country} ({currency.code})
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-6 mt-4">
              <div>
                <label className="block text-sm font-medium text-gray-700">Quantity <span className="text-red-500">*</span></label>
                <input
                  type="number"
                  placeholder="Enter Quantity"
                  value={newQuote.quantity}
                  onChange={(e) => setNewQuote({ ...newQuote, quantity: e.target.value })}
                  className="border p-2 w-full mb-2"
                  style={{ borderRadius: '24px', border: '1px solid #3483BE' }}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Educational Loan <span className="text-red-500">*</span></label>
                <select
                  value={newQuote.educationLoan ? 'Yes' : 'No'}
                  onChange={(e) => setNewQuote({ ...newQuote, educationLoan: e.target.value === 'Yes' })}
                  className="border p-2 w-full mb-2"
                  style={{ borderRadius: '24px', border: '1px solid #3483BE' }}
                >
                  <option value="Yes">Yes</option>
                  <option value="No">No</option>
                </select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-6 mt-4">
              <div>
                <label className="block text-sm font-medium text-gray-700">Purpose <span className="text-red-500">*</span></label>
                <select
                  value={newQuote.purpose}
                  onChange={(e) => setNewQuote({ ...newQuote, purpose: e.target.value })}
                  className="border p-2 w-full mb-2"
                  style={{ borderRadius: '24px', border: '1px solid #3483BE' }}
                >
                  <option value="">Select Purpose</option>
                  {purposeOptions.map((purpose, index) => (
                    <option key={index} value={purpose.value}>
                      {purpose.value}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Mark Up </label>
                <input
                  type="number"
                  placeholder="Enter Mark Up (eg: 1, 0.5, 100)"
                  value={newQuote.markUp}
                  onChange={(e) => handleMarkUpChange(e)}
                  className="border p-2 w-full mb-2"
                  style={{ borderRadius: '24px', border: '1px solid #3483BE' }}
                />
              </div>
            </div>

            <GradientSubmitButton
              type="submit"
              onClick={handleGetQuote}
              loading={isQuoteLoading}
              label="Submit"
              loadingLabel="Submitting..."
              disabled={isQuoteLoading}
            />
          </div>
        </div>
      )}

      {convertionData.length > 0 && (
        <div className="fixed inset-0 bg-gray-500 bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white p-8 rounded-3xl w-[90%] md:w-[70%] lg:w-[50%] relative">

            <div className='w-full h-[800px] overflow-y-auto'>
              {/* A4 Paper-like container */}
              <div id='content-id'
                ref={targetRef}
                className="w-[210mm] mx-auto bg-white shadow-md p-16 pb-3 relative">
                {/* Watermark */}
                {/* <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 rotate-45 text-6xl font-bold text-gray-200 pointer-events-none">
                    QUOTATION
                  </div> */}

                {/* Header */}
                <div className="mb-8">
                  <div className="flex items-center justify-center">
                    <div className="flex items-center">
                      {isLogoLoading ? (
                        <div className="w-16 h-16 bg-gray-200 animate-pulse rounded-full"></div>
                      ) : (
                        companyLogo && (
                          <img src={companyLogo} alt="Company Logo" className="w-full h-16" />
                        )
                      )}
                    </div>
                  </div>
                </div>

                {/* Document Title */}
                <h2 className="text-xl font-bold text-gray-800 text-center my-6">
                  FOREX REMITTANCE QUOTATION
                </h2>

                {/* Horizontal Line */}
                <div className="border-t-2 border-blue-500 my-4"></div>

                {/* Student Information Section */}
                <div className="mb-8">
                  <h3 className="text-sm font-bold text-gray-800 mb-3">STUDENT INFORMATION</h3>

                  <div className="text-xs space-y-2">
                    <div className="flex">
                      <div className="w-full">Name: {selectedStudent?.name || 'N/A'}</div>
                    </div>

                    <div className="flex justify-between">
                      <div className="w-1/2">Email: {selectedStudent?.email || 'N/A'}</div>
                      <div className="w-1/2">Date: {new Date().toLocaleDateString()}</div>
                    </div>

                    <div className="flex justify-between">
                      <div className="w-1/2">Phone: {selectedStudent?.number || 'N/A'}</div>
                      <div className="w-1/2">Time: {new Date().toLocaleTimeString()}</div>
                    </div>
                  </div>
                </div>

                {/* Transaction Details Section */}
                <div className="mb-8">
                  <h3 className="text-sm font-bold text-gray-800 mb-3">TRANSACTION DETAILS</h3>

                  <div className="border border-gray-300">
                    <table className="w-full text-xs">
                      <thead>
                        <tr className="bg-gray-100">
                          <th className="text-left p-2 font-bold">Description</th>
                          <th className="text-left p-2 font-bold">Value</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr>
                          <td className="p-2 border-b border-gray-200">Amount Requested</td>
                          <td className="p-2 border-b border-gray-200">
                            {convertionData[0]?.quantity} {convertionData[0]?.currencyCode}
                          </td>
                        </tr>
                        <tr className="bg-gray-50">
                          <td className="p-2 border-b border-gray-200">Exchange Rate</td>
                          <td className="p-2 border-b border-gray-200">
                            1 {convertionData[0]?.currencyCode} = {convertionData[0]?.remittanceRate || 'N/A'} INR
                          </td>
                        </tr>
                        <tr>
                          <td className="p-2 border-b border-gray-200">Loan Type</td>
                          <td className="p-2 border-b border-gray-200">
                            {convertionData[0]?.educationLoan ? 'Education Loan' : 'Non-Education Loan'}
                          </td>
                        </tr>
                        <tr>
                          <td className="p-2 border-b border-gray-200">GST</td>
                          <td className="p-2 border-b border-gray-200">
                            {convertionData[0]?.GST}
                          </td>
                        </tr>
                        <tr>
                          <td className="p-2 border-b border-gray-200">TCS</td>
                          <td className="p-2 border-b border-gray-200">
                            {convertionData[0]?.TCS}
                          </td>
                        </tr>
                        <tr>
                          <td className="p-2 border-b border-gray-200">Nostro</td>
                          <td className="p-2 border-b border-gray-200">
                            {convertionData[0]?.NostroCharge}
                          </td>
                        </tr>
                        <tr>
                          <td className="p-2 border-b border-gray-200">Total Payable</td>
                          <td className="p-2 border-b border-gray-200">
                            {convertionData[0]?.TxnAmount ? `₹ ${convertionData[0]?.TxnAmount}` : 'N/A'}
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>

                {/* Notes Section */}
                <div className='flex justify-between items-center'>
                  <div className="text-xs mt-3">
                    <p className="font-bold mb-1">NOTES:</p>
                    <p>1. The above quote is generated for one-time use only and must not be shared or reused by any other remitter for making payments.</p>
                    <p>2. Supported payment modes include RTGS, NEFT, and Bank Transfer only. Cash deposits and payments made from Current Accounts are not accepted.</p>
                    <p>3. The remittance must be made from the sender’s personal Savings Bank Account in India, or that of an immediate family member (such as parents, siblings, spouse, or children). Third-party funding from unrelated accounts is strictly not permitted.</p>
                    <p>4. The payment must be made in full for the exact amount specified in the quote. Part payments or multiple transfers may be rejected or returned.</p>
                    <p>5. The given exchange rate is subject to currency market fluctuations and is valid only between 10:00 AM to 2:45 PM on the day of issuance and on a bank working day.</p>
                  </div>
                </div>

                {/* Footer */}
                <div className="mt-8 text-center text-xs italic text-gray-500">
                  <p>Thank you for choosing our services. For support, contact {companyEmail ? companyEmail : companyName}</p>
                  <p>Page 1 of 1</p>
                </div>
              </div>
            </div>

            <div className="mt-8 flex justify-end gap-2">
              <button
                onClick={() => setConvertionData([])}
                className="px-8 py-2 bg-gradient-to-b from-[#e7e6e5] to-[#a1a1a1] text-white rounded-3xl"
              >
                Close
              </button>
              <button
                onClick={() => toPDF()}
                className="px-8 py-2 bg-gradient-to-b from-[#FFA782] to-[#FC583D] text-white rounded-3xl"
              >
                Save
              </button>
            </div>

          </div>
        </div>
      )}
    </div>
  );
};

export default ForexRemittance;